import styles from "./File.module.scss"
import cx from "classnames"
import { Edit2, Trash, Upload } from "react-feather"
import { useCallback, useEffect, useState } from "react"

import { useDropzone } from "react-dropzone"
import FilePreview from "../../VirtuellerAssistent/FilePreview"
import FieldDescription from "../FieldDescription"

import Cropper from "react-cropper"
import "cropperjs/dist/cropper.css"
import Button from "../../../Button"

const FileField = ({ field, className, methods }) => {
  const { register, watch, setValue, unregister } = methods
  const { name, description } = field

  const files = watch(name)

  const onDrop = useCallback(
    (droppedFiles) => {
      if (!!files?.length > 0) {
        setValue(name, [...files, ...droppedFiles], { shouldValidate: true })
      } else {
        setValue(name, droppedFiles, { shouldValidate: true })
      }
    },
    [files, setValue, name]
  )

  const removeFile = (event, index) => {
    event.preventDefault()
    event.stopPropagation()

    const updatedFiles = structuredClone(files)
    updatedFiles.splice(index, 1)

    setValue(name, updatedFiles)
  }

  const [cropImage, setCropImage] = useState(null)
  const [cropImageIndex, setCropImageIndex] = useState(null)
  const [cropper, setCropper] = useState()

  const editFile = (e, index) => {
    e.preventDefault()
    e.stopPropagation()

    setCropImageIndex(index)

    if (files?.[index]?.blob) {
      setCropImage(files[index].b64Data)
    } else {
      const reader = new FileReader()
      reader.onload = () => {
        setCropImage(reader.result)
      }
      reader.readAsDataURL(files[index])
    }
  }

  const getCropData = (e) => {
    e.preventDefault()
    e.stopPropagation()

    if (typeof cropper !== "undefined" && files?.[cropImageIndex]) {
      const canvas = cropper.getCroppedCanvas()
      const filename = files[cropImageIndex]?.name

      // remove original image
      removeFile(e, cropImageIndex)

      // add cropped image
      canvas.toBlob((blob) => {
        const currentFiles = watch(name)
        setValue(
          name,
          [
            ...currentFiles,
            {
              type: "image/jpeg",
              name: filename,
              blob,
              b64Data: canvas.toDataURL(),
            },
          ],
          { shouldValidate: false }
        )
      })

      setCropImage(null)
      setCropImageIndex(null)
    }
  }

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      "image/*": [],
      "drawing/*": [],
      "video/*": [],
      "text/*": [],
      "audio/*": [],
      "application/dxf": ['.dxf'],
      "application/acad": ['.dwg'],
      "application/vnd*": ['.vwx'],
      "application/pdf": [],
      "application/msexcel": [],
      "application/mspowerpoint": [],
      "application/msword": [],
      "application/x-koan": ['.skp'], // sketchup
    },
  })

  useEffect(() => {
    register(name)
    return () => {
      unregister(name)
    }
  }, [register, unregister, name])

  return (
    <div className="relative">
      {description && (
        <div className="absolute flex justify-end w-full right-0 bottom-full mb-1">
          <FieldDescription description={description} />
        </div>
      )}
      <div {...getRootProps()} aria-label="File Upload">
        <input {...getInputProps()} />
        <div className={cx(styles.inputWrapper, className)}>
          <div className="flex flex-col items-center text-body-small">
            <div className="border border-brand-blue-600 rounded-full p-3">
              <Upload className="w-5 h-5" />
            </div>
            <div className=" font-semibold mt-2">Datei(en) auswählen</div>
          </div>

          {!!files?.length && (
            <div className="mt-8 flex flex-col items-center w-full">
              {files.map((file, i) => {
                const isImage = file?.type?.startsWith("image/") || false

                return (
                  <div
                    key={i}
                    className="flex items-center justify-between mb-2 last:mb-0 w-full"
                  >
                    <FilePreview
                      name={file.name}
                      url={
                        file?.blob ? file.b64Data : URL.createObjectURL(file)
                      }
                      isImage={isImage}
                    />

                    <div className="flex items-center">
                      {isImage ? (
                        <button onClick={(e) => editFile(e, i)}>
                          <Edit2 className="ml-2 w-5 h-5" />
                        </button>
                      ) : (
                        <div></div>
                      )}
                      <button onClick={(e) => removeFile(e, i)}>
                        <Trash className="ml-2 w-5 h-5" />
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>

      <div
        className={cx(styles.cropModal, {
          [styles.active]: cropImage,
        })}
      >
        <div className={styles.cropModalInner}>
          <div className={styles.cropper}>
            <Cropper
              style={{
                height: "100%",
                maxHeight: "80vh",
                overflow: "hidden",
                width: "100%",
              }}
              zoomTo={0.5}
              initialAspectRatio={1}
              preview=".img-preview"
              src={cropImage}
              viewMode={1}
              minCropBoxHeight={10}
              minCropBoxWidth={10}
              background={false}
              responsive={false}
              autoCropArea={1}
              checkOrientation={false} // https://github.com/fengyuanchen/cropperjs/issues/671
              onInitialized={(instance) => {
                setCropper(instance)
              }}
              guides={true}
            />
          </div>
          <div className={styles.controls}>
            <Button
              type={"button"}
              displayType={{ value: "button_secondary" }}
              onClick={() => {
                setCropImage(null)
                setCropImageIndex(null)
              }}
              className={"mr-4"}
            >
              Abbrechen
            </Button>
            <Button
              type={"button"}
              displayType={{ value: "button_primary" }}
              iconPosition={{ value: "right" }}
              onClick={(e) => getCropData(e)}
            >
              Weiter
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default FileField
